import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * The "business" wordmark that sits under the Noor logo on the merchant
 * panel's sign-in screen (Figma node 26295:100072).
 *
 * The designer's exported vector, re-fitted to `currentColor` the same way
 * `Logo` is, so it can be coloured with a text utility rather than being
 * locked to the white it ships as: `<BusinessWordmark className="text-primary-foreground" />`.
 */
function BusinessWordmark({ className, ...props }: React.ComponentProps<'svg'>) {
  return (
    <svg
      width="100"
      height="19"
      viewBox="0 0 99.6786 18.5449"
      data-slot="business-wordmark"
      className={cn('h-[18px] w-auto text-foreground', className)}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M2.86608 11.4586C2.86608 14.5746 4.39968 16.1074 6.36068 16.1074C8.37196 16.1074 9.77986 14.3484 9.77986 11.4586C9.77986 8.64422 8.37196 6.83496 6.36068 6.83496C4.39968 6.83496 2.86608 8.34267 2.86608 11.4586ZM0 0H2.99178V6.50829C3.79629 5.17648 5.20419 4.39749 7.01434 4.39749C10.2324 4.39749 12.8973 7.1365 12.8973 11.4586C12.8973 15.8058 10.2324 18.5449 7.01434 18.5449C5.20419 18.5449 3.79629 17.7659 2.99178 16.4341V18.2182H0V0Z"
        fill="currentColor"
      />
      <path
        d="M23.1889 18.2182V16.3587C22.4095 17.7659 21.0267 18.5449 19.1914 18.5449C15.9734 18.5449 14.6661 16.4341 14.6661 13.2176V4.69904H17.6578V12.4889C17.6578 14.3233 17.9092 16.1074 20.1468 16.1074C22.3843 16.1074 23.1889 14.3484 23.1889 11.9109V4.69904H26.1806V18.2182H23.1889Z"
        fill="currentColor"
      />
      <path
        d="M39.5798 8.39293H36.764C36.4372 7.16163 35.3058 6.68419 33.7974 6.68419C32.49 6.68419 31.2833 7.26215 31.2833 8.16677C31.2833 9.0714 31.9118 9.54884 33.0934 9.775L35.0042 10.1519C37.7445 10.6796 39.8312 11.534 39.8312 14.1725C39.8312 16.9869 37.1663 18.5449 34.0991 18.5449C30.7805 18.5449 28.5681 16.6351 28.2412 14.0971H31.1576C31.4341 15.5043 32.4146 16.2833 34.1996 16.2833C35.7835 16.2833 36.9149 15.6299 36.9149 14.5243C36.9149 13.4186 35.9092 12.9412 34.5768 12.6648L32.5403 12.2627C30.2525 11.7853 28.4675 10.8053 28.4675 8.31755C28.4675 5.93033 31.057 4.39749 33.9734 4.39749C36.6132 4.39749 39.0267 5.67905 39.5798 8.39293Z"
        fill="currentColor"
      />
      <path
        d="M41.7763 0H44.7681V3.01542H41.7763V0ZM41.7763 4.69904H44.7681V18.2182H41.7763V4.69904Z"
        fill="currentColor"
      />
      <path
        d="M50.6242 4.69904V6.58368C51.4036 5.17648 52.8115 4.39749 54.7222 4.39749C57.9905 4.39749 59.323 6.50829 59.323 9.72474V18.2182H56.3312V10.4535C56.3312 8.61909 56.0798 6.83496 53.7668 6.83496C51.4538 6.83496 50.6242 8.59396 50.6242 11.0314V18.2182H47.6324V4.69904H50.6242Z"
        fill="currentColor"
      />
      <path
        d="M74.1528 12.313H64.0713C64.2724 14.8258 66.0323 16.0571 67.7419 16.0571C69.3258 16.0571 70.5325 15.454 71.0605 14.1725H74.002C73.3986 16.3335 71.337 18.5449 67.8676 18.5449C63.5182 18.5449 61.0292 15.253 61.0292 11.3581C61.0292 7.28727 63.8199 4.39749 67.6665 4.39749C71.8147 4.39749 74.4294 7.78985 74.1528 12.313ZM67.6665 6.73445C66.2586 6.73445 64.3478 7.58882 64.0713 10.1268H71.1108C71.0354 8.016 69.4263 6.73445 67.6665 6.73445Z"
        fill="currentColor"
      />
      <path
        d="M86.6966 8.39293H83.8808C83.554 7.16163 82.4227 6.68419 80.9142 6.68419C79.6069 6.68419 78.4001 7.26215 78.4001 8.16677C78.4001 9.0714 79.0286 9.54884 80.2103 9.775L82.121 10.1519C84.8613 10.6796 86.948 11.534 86.948 14.1725C86.948 16.9869 84.2831 18.5449 81.2159 18.5449C77.8973 18.5449 75.6849 16.6351 75.358 14.0971H78.2744C78.5509 15.5043 79.5314 16.2833 81.3165 16.2833C82.9003 16.2833 84.0317 15.6299 84.0317 14.5243C84.0317 13.4186 83.026 12.9412 81.6936 12.6648L79.6571 12.2627C77.3693 11.7853 75.5843 10.8053 75.5843 8.31755C75.5843 5.93033 78.1738 4.39749 81.0902 4.39749C83.73 4.39749 86.1435 5.67905 86.6966 8.39293Z"
        fill="currentColor"
      />
      <path
        d="M99.4272 8.39293H96.6114C96.2846 7.16163 95.1532 6.68419 93.6448 6.68419C92.3375 6.68419 91.1307 7.26215 91.1307 8.16677C91.1307 9.0714 91.7592 9.54884 92.9408 9.775L94.8516 10.1519C97.5919 10.6796 99.6786 11.534 99.6786 14.1725C99.6786 16.9869 97.0137 18.5449 93.9465 18.5449C90.6279 18.5449 88.4155 16.6351 88.0886 14.0971H91.005C91.2815 15.5043 92.262 16.2833 94.047 16.2833C95.6309 16.2833 96.7623 15.6299 96.7623 14.5243C96.7623 13.4186 95.7566 12.9412 94.4242 12.6648L92.3877 12.2627C90.0999 11.7853 88.3149 10.8053 88.3149 8.31755C88.3149 5.93033 90.9044 4.39749 93.8208 4.39749C96.4606 4.39749 98.8741 5.67905 99.4272 8.39293Z"
        fill="currentColor"
      />
    </svg>
  );
}

export { BusinessWordmark };
